import {ApiClient} from '@hconnect/apiclient'
import {type AggregatedWorkOrderSummaryDto} from '@hconnect/common/types/workOrders.types'
import {useQuery, UseQueryOptions} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {Moment} from 'moment'
import qs from 'qs'
import {useEffect} from 'react'

import {WorkOrdersQueryKey} from '../../types'

const QueryKeyPrefix = WorkOrdersQueryKey.WorkOrdersSummary

type WorkOrdersSummaryParams = {
  plantId: string
  from: Moment
  to: Moment
  workOrderTypes?: string[]
}

export const useWorkOrdersSummary = (
  {plantId, ...searchParams}: WorkOrdersSummaryParams,
  options?: Omit<
    UseQueryOptions<AggregatedWorkOrderSummaryDto, AxiosError>,
    'queryKey' | 'queryFn'
  > & {
    onError?: (error: AxiosError) => void
  }
) => {
  const from = searchParams.from.toISOString()
  const to = searchParams.to.toISOString()

  const query = useQuery<AggregatedWorkOrderSummaryDto, AxiosError>({
    queryKey: [QueryKeyPrefix, plantId, from, to],
    queryFn: async ({signal}) => {
      const response = await ApiClient.axiosInstance.get<AggregatedWorkOrderSummaryDto>(
        `/work-orders/${plantId}/summary`,
        {
          params: {
            from,
            to,
            workOrderTypes: searchParams.workOrderTypes
          },
          paramsSerializer: (params) => qs.stringify(params, {arrayFormat: 'repeat'}),
          signal
        }
      )

      return response.data
    },
    ...options
  })

  const onError = options?.onError

  useEffect(() => {
    if (query.error) {
      onError?.(query.error)
    }
  }, [query.error, onError])

  return query
}
